// HowToPlay — a floating help pill that opens a one-page controls overlay.
// Sits in the bottom-left utility stack (just above the docked navigation
// bar) so it never overlaps the expanded top-left Sports Arena widget.
// Paired with the CampusMap pill directly above it. The overlay explains
// the desktop and mobile controls for all four sports in about ten lines
// of copy — small enough that no visitor has to scroll.

import React, { useState } from "react";
import { HelpCircle, X, MousePointerClick, Keyboard, Move, Trophy, Users, MapPin } from "lucide-react";

import { COLORS } from "../constants";

export default function HowToPlay({ isTouch }) {
  const [open, setOpen] = useState(false);

  // Bottom-left stack, just above the docked nav bar (bottom:78 + ~48px height).
  // On touch, sit above the walk-mode MobileJoystick (bottom:32, size:140).
  // Map sits ON TOP, How to Play sits BELOW it — so HowToPlay gets the
  // smaller `bottom` offset (closer to the nav bar).
  const bottomOffset = isTouch ? 190 : 138;

  return (
    <>
      {/* Floating trigger — bottom-left, below the CampusMap pill */}
      <button
        data-testid="howtoplay-btn"
        onClick={() => setOpen(true)}
        aria-label="How to play"
        style={{
          position: "fixed",
          left: 20,
          bottom: bottomOffset,
          // z-index kept below the modal overlays (91/92) so an expanded
          // modal — or the ArenaHUD expanded widget — cleanly sits above.
          zIndex: 40,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderRadius: 20,
          background: "linear-gradient(180deg, rgba(11,21,48,0.85) 0%, rgba(6,14,32,0.85) 100%)",
          border: `1px solid ${COLORS.gold}`,
          color: "#f7dc95",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: "uppercase",
          cursor: "pointer",
          boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
          backdropFilter: "blur(6px)",
        }}
      >
        <HelpCircle size={13} />
        How to Play
      </button>

      {open && (
        <div
          data-testid="howtoplay-overlay"
          onClick={() => setOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 92,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(11,21,48,0.55)",
            backdropFilter: "blur(4px)",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: 520,
              width: "100%",
              maxHeight: "88vh",
              overflowY: "auto",
              padding: "26px 26px 22px",
              borderRadius: 18,
              background:
                "linear-gradient(180deg, rgba(11,21,48,0.96) 0%, rgba(6,14,32,0.96) 100%)",
              border: `1px solid ${COLORS.gold}`,
              boxShadow: "0 24px 56px rgba(0,0,0,0.5), 0 0 0 6px rgba(230,180,80,0.08)",
              color: "#f3ead4",
            }}
          >
            <button
              data-testid="howtoplay-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                padding: 6,
                background: "transparent",
                border: "none",
                color: "#c9b382",
                cursor: "pointer",
                opacity: 0.8,
              }}
            >
              <X size={18} />
            </button>

            <div
              style={{
                fontSize: 11,
                letterSpacing: 2,
                color: "#c9b382",
                fontWeight: 700,
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Sports Arena · How to Play
            </div>
            <h2
              style={{
                margin: 0,
                fontFamily: "'Cormorant Garamond','Playfair Display',serif",
                fontSize: 28,
                fontWeight: 600,
                color: "#f7dc95",
              }}
            >
              Gameday, in ten seconds.
            </h2>

            <Row icon={<MapPin size={16} />} title="Get to the arena">
              Tap <b>PLAY NOW</b> from the landing card, or open the Sports
              Arena panel (top-left) and tap <b>Go to Arena</b>. You'll land
              at the entrance plaza with three fields fanning out — basketball
              to the north, football to the south, soccer to the east.
            </Row>

            {isTouch ? (
              <>
                <Row icon={<Move size={16} />} title="Move (mobile)">
                  Use the bottom-left <b>joystick</b> to walk. The <b>FIRE</b>{" "}
                  button on the bottom-right shoots the ball — its label
                  changes with the sport (SHOOT / KICK / SNAP / CATCH /
                  TACKLE).
                </Row>
              </>
            ) : (
              <>
                <Row icon={<Keyboard size={16} />} title="Move (desktop)">
                  Click <b>WALK</b> in the top-right and use{" "}
                  <b>W · A · S · D</b> or the arrow keys to run. Drag with the
                  mouse to look around.
                </Row>
                <Row icon={<MousePointerClick size={16} />} title="Shoot">
                  <b>Click anywhere on the court</b> to shoot / kick / throw
                  toward that spot. The ball respects real physics — angle
                  and distance matter.
                </Row>
              </>
            )}

            <Row icon={<Trophy size={16} />} title="Score">
              <b>Basketball:</b> click above the rim and let the ball drop
              through the hoop. <b>Soccer:</b> kick into the goal net.{" "}
              <b>Football:</b> as QB tap to snap, then tap a downfield spot
              to throw — teammates auto-catch anything they can reach.
            </Row>

            <Row icon={<Users size={16} />} title="Play with others">
              Toggle <b>Match Invite</b> in the Sports Arena panel to let
              other visitors' kicks influence your ball. Peer players appear
              as glowing ghost avatars in your team colour.
            </Row>

            <div style={{ marginTop: 16, fontSize: 12, color: "#c9b382", opacity: 0.8 }}>
              Tip · switch sports from the panel; the ball respawns at the
              new field's centre automatically.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ icon, title, children }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        margin: "16px 0 0",
        paddingTop: 14,
        borderTop: "1px solid rgba(230,180,80,0.14)",
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 10,
          background: "rgba(230,180,80,0.12)",
          border: "1px solid rgba(230,180,80,0.32)",
          color: "#f7dc95",
        }}
      >
        {icon}
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "#f7dc95",
            marginBottom: 4,
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "#dcd3b8" }}>
          {children}
        </div>
      </div>
    </div>
  );
}
